import { Controller } from "@hotwired/stimulus";
import { format, parseISO, addMinutes } from "date-fns";

export default class extends Controller {
  static targets = [
    "modal",
    "serviceSelect",
    "serviceInfo",
    "servicePriceDisplay",
    "serviceDurationDisplay",
    "servicePriceField",
    "serviceDurationField",
    "startTimeField",
    "endTimeField",
    "appointmentDateHidden",
    "appointmentTimeHidden",
    "appointmentTimeField",
    "endTimeDisplay",
    "staffSelect",
    "locationSelect",
  ];

  connect() {
    // Set up event delegation for dynamically added elements
    this.setupEventDelegation();
  }

  setupEventDelegation() {
    // Use event delegation on the controller element for edit buttons
    this.element.addEventListener("click", (event) => {
      // Check for edit buttons with data-action containing openEditModal
      const editButton = event.target.closest('[data-action*="openEditModal"]');
      if (editButton && editButton.hasAttribute("data-appointment-id")) {
        event.preventDefault();
        this.handleEditButtonClick(editButton);
        return;
      }

      // Also handle direct clicks on buttons with the action
      if (event.target.matches('button[data-action*="openEditModal"]')) {
        event.preventDefault();
        this.handleEditButtonClick(event.target);
      }
    });
  }

  handleEditButtonClick(button) {
    const appointmentId = button.dataset.appointmentId;

    fetch(`/operations/appointments/${appointmentId}/edit`, {
      method: "GET",
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        const modal = document.getElementById("appointment-modal");
        modal.innerHTML = html;
        modal.classList.remove("hidden");
        document.body.classList.add("overflow-hidden");

        // Auto-load services if staff is already selected
        this.autoLoadServicesForEdit();
        // Filter staff to the appointment's location after targets bind.
        requestAnimationFrame(() => this.filterStaffByLocation());
      })
      .catch((error) => {
        console.error("Error loading edit form:", error);
      });
  }

  openAddModal(event) {
    event.preventDefault();

    // Forward ?location_id from current URL so the new form can prefill it
    const currentLocationId = new URLSearchParams(window.location.search).get("location_id");
    const url = currentLocationId
      ? `/operations/appointments/new?location_id=${encodeURIComponent(currentLocationId)}`
      : "/operations/appointments/new";

    // Make request to get the new appointment form
    fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        const modal = document.getElementById("appointment-modal");
        modal.innerHTML = html;
        modal.classList.remove("hidden");
        document.body.classList.add("overflow-hidden");
        // After Stimulus rebinds targets in injected HTML, filter staff to
        // the currently-selected location.
        requestAnimationFrame(() => this.filterStaffByLocation());
      })
      .catch((error) => {
        console.error("Error loading add form:", error);
      });
  }

  closeModal(event) {
    event.preventDefault();
    const modal = document.getElementById("appointment-modal");
    modal.innerHTML = "";
    modal.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
  }

  // Keep the original method for backwards compatibility but it will be handled by delegation
  openEditModal(event) {
    event.preventDefault();
    this.handleEditButtonClick(event.currentTarget);
  }

  // Auto-load services when modal opens with pre-selected staff
  autoLoadServicesForEdit() {
    // Wait a brief moment for the DOM to be fully ready
    setTimeout(() => {
      const staffSelect = document.querySelector(
        'select[name="appointment[staff_id]"]',
      );
      const serviceSelect = document.querySelector(
        'select[name="appointment[service_id]"]',
      );

      if (!staffSelect || !serviceSelect) {
        return;
      }

      const selectedStaffId = staffSelect.value;
      // Get service_id from data attribute since form field is empty initially
      const selectedServiceId = serviceSelect.dataset.appointmentServiceId;

      if (selectedStaffId) {
        // Load services for the selected staff
        fetch(`/operations/appointments/staff_services/${selectedStaffId}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        })
          .then((response) => response.json())
          .then((data) => {
            this.populateServicesForEdit(data.services, selectedServiceId);
          })
          .catch((error) => {
            console.error("Error auto-loading staff services:", error);
          });

        // Also load time slots for the selected staff with current date/time
        const startTimeField = document.querySelector(
          '[data-target="appointments.startTimeField"]',
        );

        // Get appointment ID if editing (from form action URL)
        const form = document.querySelector('form[action*="appointments"]');
        const appointmentId =
          form?.action?.match(/appointments\/(\d+)/)?.[1] || "";

        const frame = document.getElementById("time-selection-frame");
        if (frame) {
          let dateStr, timeStr;
          if (startTimeField && startTimeField.value) {
            const startDateTime = parseISO(startTimeField.value);
            dateStr = format(startDateTime, "yyyy-MM-dd");
            timeStr = format(startDateTime, "HH:mm");
          }
          frame.src = this.buildTimeSlotsUrl({
            staffId: selectedStaffId,
            date: dateStr,
            time: timeStr,
            appointmentId,
          });
        }
      }
    }, 100);
  }

  // Sync location select to staff's location when a staff is chosen.
  // Phase 1 invariant: an appointment's location matches its staff's location.
  syncLocationFromStaff(event) {
    if (!this.hasLocationSelectTarget) return;
    const option = event.target.selectedOptions[0];
    const locationId = option?.dataset.locationId;
    if (locationId) {
      this.locationSelectTarget.value = locationId;
    }
  }

  // Rebuild the staff dropdown to show only staff at the selected location.
  // Source of truth: the full staff list embedded as JSON on the select's
  // `data-all-staff-options` attribute. Runs whenever location changes.
  filterStaffByLocation() {
    if (!this.hasStaffSelectTarget) return;
    const select = this.staffSelectTarget;
    const raw = select.dataset.allStaffOptions;
    if (!raw) return;

    const locationId = this.hasLocationSelectTarget
      ? this.locationSelectTarget.value
      : select
          .closest("form")
          ?.querySelector("input[name='appointment[location_id]']")?.value;

    let allOptions;
    try {
      allOptions = JSON.parse(raw);
    } catch (_e) {
      return;
    }

    const filtered = locationId
      ? allOptions.filter((o) => String(o.location_id) === String(locationId))
      : allOptions;

    const previousValue = select.value;
    select.innerHTML = '<option value="">Select staff member</option>';
    filtered.forEach((o) => {
      const opt = document.createElement("option");
      opt.value = o.id;
      opt.textContent = o.name;
      opt.dataset.locationId = o.location_id;
      select.appendChild(opt);
    });

    if (filtered.some((o) => String(o.id) === String(previousValue))) {
      select.value = previousValue;
    } else if (previousValue) {
      // Previously selected staff no longer in scope — clear dependent fields
      this.clearServices();
    }
  }

  // Load services when staff is selected
  loadStaffServices(event) {
    const staffId = event.target.value;

    if (!staffId) {
      this.clearServices();
      return;
    }

    fetch(`/operations/appointments/staff_services/${staffId}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        this.populateServices(data.services);
      })
      .catch((error) => {
        console.error("Error loading staff services:", error);
        this.clearServices();
      });
  }

  // Load time slots turbo frame when staff is selected
  loadTimeSlots(event) {
    const staffId = event.target.value;

    if (!staffId) {
      const frame = document.getElementById("time-selection-frame");
      frame.innerHTML =
        '<div class="col-span-2"><p class="text-sm text-gray-500 italic">Please select a staff member to choose date and time</p></div>';
      return;
    }

    const frame = document.getElementById("time-selection-frame");
    frame.src = this.buildTimeSlotsUrl({ staffId });
  }

  // Build time_slots_form URL with all relevant params
  buildTimeSlotsUrl({ staffId, date, time, appointmentId } = {}) {
    const params = new URLSearchParams();
    if (staffId) params.set("staff_id", staffId);
    if (date) params.set("date", date);
    if (time) params.set("time", time);
    if (appointmentId) params.set("appointment_id", appointmentId);

    // Include service_id if a service is selected
    const serviceSelect = document.querySelector(
      'select[name="appointment[service_id]"]',
    );
    if (serviceSelect?.value) params.set("service_id", serviceSelect.value);

    return `/operations/appointments/time_slots_form?${params.toString()}`;
  }

  // Reload time slots with current context (called when service changes)
  reloadTimeSlots() {
    const staffSelect = document.querySelector(
      'select[name="appointment[staff_id]"]',
    );
    const staffId = staffSelect?.value;
    if (!staffId) return;

    const frame = document.getElementById("time-selection-frame");
    if (!frame) return;

    // Get current date from the visible date picker first, then fall back to hidden field
    const visibleDateInput = document.querySelector(
      'input[name="appointment_date"]',
    );
    const hiddenDateInput = document.querySelector(
      'input[name="appointment[appointment_date]"]',
    );
    const date = visibleDateInput?.value || hiddenDateInput?.value;

    // Get current selected time from the visible select dropdown first, then fall back to hidden field
    const visibleTimeSelect = document.querySelector(
      'select[name="appointment_time"]',
    );
    const hiddenTimeInput = document.querySelector(
      'input[name="appointment[appointment_time]"]',
    );
    const time = visibleTimeSelect?.value || hiddenTimeInput?.value;

    // Get appointment ID if editing
    const form = document.querySelector('form[action*="appointments"]');
    const appointmentId = form?.action?.match(/appointments\/(\d+)/)?.[1] || "";

    const url = this.buildTimeSlotsUrl({ staffId, date, time, appointmentId });

    fetch(url, {
      headers: {
        Accept: "text/html",
        "Turbo-Frame": "time-selection-frame",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newFrame = doc.getElementById("time-selection-frame");
        if (newFrame && frame) {
          frame.innerHTML = newFrame.innerHTML;
        }
      })
      .catch((error) => {
        console.error("Error reloading time slots:", error);
      });
  }

  // Populate the service dropdown with options
  populateServices(services) {
    const serviceSelect = this.serviceSelectTarget;
    const prompt = serviceSelect.querySelector('option[value=""]');

    // Clear existing options except prompt
    serviceSelect.innerHTML = prompt
      ? prompt.outerHTML
      : '<option value="">Select service</option>';

    // Add service options
    services.forEach((service) => {
      const option = document.createElement("option");
      option.value = service.id;
      option.textContent = `${service.name} - ${service.price_display} (${service.duration_display})`;
      option.dataset.price = service.effective_price;
      option.dataset.duration = service.duration_minutes;
      option.dataset.buffer = service.buffer_minutes || 0;
      option.dataset.priceDisplay = service.price_display;
      option.dataset.durationDisplay = service.duration_display;
      serviceSelect.appendChild(option);
    });

    // Enable the service dropdown
    serviceSelect.disabled = false;
    serviceSelect.querySelector("option").textContent = "Select service";
  }

  // Populate services for edit mode, preserving current selection
  populateServicesForEdit(services, selectedServiceId) {
    const serviceSelect = document.querySelector(
      'select[name="appointment[service_id]"]',
    );
    if (!serviceSelect) return;

    // Clear existing options
    serviceSelect.innerHTML = '<option value="">Select service</option>';

    // Add service options
    services.forEach((service) => {
      const option = document.createElement("option");
      option.value = service.id;
      option.textContent = `${service.name} - ${service.price_display} (${service.duration_display})`;
      option.dataset.price = service.effective_price;
      option.dataset.duration = service.duration_minutes;
      option.dataset.buffer = service.buffer_minutes || 0;
      option.dataset.priceDisplay = service.price_display;
      option.dataset.durationDisplay = service.duration_display;

      // Pre-select the current service if it matches
      if (
        selectedServiceId &&
        service.id.toString() === selectedServiceId.toString()
      ) {
        option.selected = true;

        // Update service info display for the selected service
        setTimeout(() => {
          this.updateServiceInfoForEdit(option);
          // Load add-ons for the selected service
          this.loadServiceAddOns(service.id);
        }, 50);
      }

      serviceSelect.appendChild(option);
    });

    // Enable the service dropdown
    serviceSelect.disabled = false;
  }

  // Clear services when no staff selected
  clearServices() {
    if (this.hasServiceSelectTarget) {
      this.serviceSelectTarget.innerHTML =
        '<option value="">Select service (choose staff first)</option>';
      this.serviceSelectTarget.disabled = true;
    }
    this.hideServiceInfo();
  }

  // Calculate end time when service or start time changes
  calculateEndTime(event) {
    let durationMinutes = null;

    // Check if this is a service selection event
    const serviceSelect = document.querySelector(
      'select[name="appointment[service_id]"]',
    );
    const isServiceSelection =
      event.target.name === "appointment[service_id]" ||
      event.target === serviceSelect ||
      (this.hasServiceSelectTarget &&
        event.target === this.serviceSelectTarget);

    if (isServiceSelection) {
      const selectedOption = event.target.selectedOptions[0];

      if (selectedOption && selectedOption.dataset.duration) {
        durationMinutes = parseInt(selectedOption.dataset.duration);
        this.updateServiceInfo(selectedOption);
        this.updateHiddenFields(selectedOption);
        // Load add-ons for selected service via Turbo Frame
        this.loadServiceAddOns(selectedOption.value);
      } else {
        // Clear add-ons when no service selected
        this.clearServiceAddOns();
      }

      // Reload time slots with the selected service's duration
      this.reloadTimeSlots();
    } else {
      // If triggered by start time change, use existing service duration
      if (this.hasServiceDurationFieldTarget) {
        durationMinutes = parseInt(this.serviceDurationFieldTarget.value);
      } else {
        const durationField = document.querySelector(
          '[data-target="appointments.serviceDurationField"]',
        );
        durationMinutes = parseInt(durationField?.value);
      }
    }

    // Only calculate end time if we have start time and duration
    const startTimeField = this.hasStartTimeFieldTarget
      ? this.startTimeFieldTarget
      : document.querySelector('[data-target="appointments.startTimeField"]');
    const endTimeField = this.hasEndTimeFieldTarget
      ? this.endTimeFieldTarget
      : document.querySelector('[data-target="appointments.endTimeField"]');

    if (!startTimeField || !endTimeField) return;

    const startTime = startTimeField.value;
    if (!startTime) return;

    if (durationMinutes) {
      // Parse start time and add duration using date-fns
      const start = parseISO(startTime);
      const end = addMinutes(start, durationMinutes);

      // Format for datetime-local input (YYYY-MM-DDTHH:mm)
      endTimeField.value = format(end, "yyyy-MM-dd'T'HH:mm");
    }
  }

  // Update service info display
  updateServiceInfo(selectedOption) {
    // Try using Stimulus targets first, fall back to DOM selectors
    let serviceInfo, priceDisplay, durationDisplay;

    if (this.hasServiceInfoTarget) {
      serviceInfo = this.serviceInfoTarget;
      priceDisplay = this.servicePriceDisplayTarget;
      durationDisplay = this.serviceDurationDisplayTarget;
    } else {
      // Fallback to DOM selectors for cases where targets aren't connected
      serviceInfo = document.querySelector(
        '[data-target="appointments.serviceInfo"]',
      );
      priceDisplay = document.querySelector(
        '[data-target="appointments.servicePriceDisplay"]',
      );
      durationDisplay = document.querySelector(
        '[data-target="appointments.serviceDurationDisplay"]',
      );
    }

    if (
      serviceInfo &&
      priceDisplay &&
      durationDisplay &&
      selectedOption &&
      selectedOption.value
    ) {
      priceDisplay.textContent = selectedOption.dataset.priceDisplay;
      durationDisplay.textContent = this.formatDurationWithBuffer(
        selectedOption.dataset.durationDisplay,
        selectedOption.dataset.buffer,
      );
      serviceInfo.classList.remove("hidden");
    } else {
      this.hideServiceInfo();
    }
  }

  // Update service info display for edit mode (using selectors since targets may not be connected)
  updateServiceInfoForEdit(selectedOption) {
    const serviceInfo = document.querySelector(
      '[data-target="appointments.serviceInfo"]',
    );
    const priceDisplay = document.querySelector(
      '[data-target="appointments.servicePriceDisplay"]',
    );
    const durationDisplay = document.querySelector(
      '[data-target="appointments.serviceDurationDisplay"]',
    );
    const priceField = document.querySelector(
      '[data-target="appointments.servicePriceField"]',
    );
    const durationField = document.querySelector(
      '[data-target="appointments.serviceDurationField"]',
    );

    if (
      serviceInfo &&
      priceDisplay &&
      durationDisplay &&
      selectedOption &&
      selectedOption.value
    ) {
      priceDisplay.textContent = selectedOption.dataset.priceDisplay;
      durationDisplay.textContent = this.formatDurationWithBuffer(
        selectedOption.dataset.durationDisplay,
        selectedOption.dataset.buffer,
      );
      serviceInfo.classList.remove("hidden");

      // Update hidden fields
      if (priceField) priceField.value = selectedOption.dataset.price;
      if (durationField) durationField.value = selectedOption.dataset.duration;
    }
  }

  formatDurationWithBuffer(display, buffer) {
    const bufferMinutes = parseInt(buffer || 0);
    return bufferMinutes > 0
      ? `${display} + ${bufferMinutes} min buffer`
      : display;
  }

  // Update hidden price and duration fields
  updateHiddenFields(selectedOption) {
    // Try using Stimulus targets first, fall back to DOM selectors
    let priceField, durationField;

    if (this.hasServicePriceFieldTarget && this.hasServiceDurationFieldTarget) {
      priceField = this.servicePriceFieldTarget;
      durationField = this.serviceDurationFieldTarget;
    } else {
      priceField = document.querySelector(
        '[data-target="appointments.servicePriceField"]',
      );
      durationField = document.querySelector(
        '[data-target="appointments.serviceDurationField"]',
      );
    }

    if (selectedOption && selectedOption.value && priceField && durationField) {
      priceField.value = selectedOption.dataset.price;
      durationField.value = selectedOption.dataset.duration;
    } else if (priceField && durationField) {
      priceField.value = "";
      durationField.value = "";
    }
  }

  // Hide service info
  hideServiceInfo() {
    let serviceInfo;

    if (this.hasServiceInfoTarget) {
      serviceInfo = this.serviceInfoTarget;
    } else {
      serviceInfo = document.querySelector(
        '[data-target="appointments.serviceInfo"]',
      );
    }

    if (serviceInfo) {
      serviceInfo.classList.add("hidden");
    }
  }

  // Handle date change - update hidden field and reload time slots
  handleDateChange(event) {
    const selectedDate = event.target.value;

    // Update hidden date field for form submission
    const hiddenDateField = this.hasAppointmentDateHiddenTarget
      ? this.appointmentDateHiddenTarget
      : document.querySelector('input[name="appointment[appointment_date]"]');

    if (hiddenDateField) {
      hiddenDateField.value = selectedDate;
    }

    // Reload time slots for the new date to show updated availability
    const staffId =
      event.target.dataset.staffId ||
      document.querySelector('select[name="appointment[staff_id]"]')?.value;

    if (staffId && selectedDate) {
      const frame = document.getElementById("time-selection-frame");

      // Get appointment ID if editing (from form action URL)
      const form = document.querySelector('form[action*="appointments"]');
      const appointmentId =
        form?.action?.match(/appointments\/(\d+)/)?.[1] || "";

      const url = this.buildTimeSlotsUrl({
        staffId,
        date: selectedDate,
        appointmentId,
      });

      // Fetch new time slots and replace frame content
      // Using fetch instead of src to ensure reliable reload
      fetch(url, {
        headers: {
          Accept: "text/html",
          "Turbo-Frame": "time-selection-frame",
        },
      })
        .then((response) => response.text())
        .then((html) => {
          // Parse the response and extract the turbo-frame content
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newFrame = doc.getElementById("time-selection-frame");
          if (newFrame && frame) {
            frame.innerHTML = newFrame.innerHTML;
          }
        })
        .catch((error) => {
          console.error("Error reloading time slots:", error);
        });
    }

    // If time is already selected, recalculate end time
    const timeField = document.querySelector('select[name="appointment_time"]');
    if (timeField && timeField.value) {
      this.handleTimeChange({ target: timeField });
    }
  }

  // Handle time change - update hidden field and trigger end time calculation
  handleTimeChange(event) {
    const selectedTime = event.target.value;

    // Update hidden time field for form submission
    const hiddenTimeField = this.hasAppointmentTimeHiddenTarget
      ? this.appointmentTimeHiddenTarget
      : document.querySelector('input[name="appointment[appointment_time]"]');

    if (hiddenTimeField) {
      hiddenTimeField.value = selectedTime;
    }

    // Get date field value
    const dateField = document.querySelector('input[name="appointment_date"]');
    const selectedDate = dateField?.value;

    // If both date and time are selected, construct datetime and calculate end time
    if (selectedDate && selectedTime) {
      // Combine date and time into datetime-local format: "YYYY-MM-DDTHH:MM"
      const startTimeString = `${selectedDate}T${selectedTime}`;

      // Update start_time field if it exists (for backwards compatibility)
      const startTimeField = this.hasStartTimeFieldTarget
        ? this.startTimeFieldTarget
        : document.querySelector('[data-target="appointments.startTimeField"]');

      if (startTimeField) {
        startTimeField.value = startTimeString;

        // Trigger end time calculation with the constructed datetime
        const syntheticEvent = {
          target: {
            name: "appointment[start_time]",
            value: startTimeString,
          },
        };
        this.calculateEndTime(syntheticEvent);
      }
    }
  }

  // Load service add-ons via Turbo Frame (Hotwire convention: use frames, not fetch)
  loadServiceAddOns(serviceId) {
    const frame = document.getElementById("service_add_ons_frame");
    if (!frame || !serviceId) return;

    // Update frame src to trigger Turbo to fetch add-ons
    frame.src = `/operations/services/${serviceId}/add_ons`;
  }

  // Clear service add-ons when no service is selected
  // Uses server endpoint to get empty state (Hotwire convention: content from server)
  clearServiceAddOns() {
    const frame = document.getElementById("service_add_ons_frame");
    if (!frame) return;

    // Load empty state from server instead of hardcoding HTML
    frame.src = "/operations/services/add_ons_empty";
  }
}
